import { existsSync, mkdirSync, readFileSync, writeFileSync } from "node:fs";
import { join } from "node:path";

import type { AgentMessage } from "@elyracode/agent-core";
import { type AssistantMessage, completeSimple, type Model } from "@elyracode/ai";

import { getAgentDir } from "../config.js";

/** Directory for memory files */
export function getMemoryDir(): string {
	return join(getAgentDir(), "memory");
}

/** Get the memory file path for a given project cwd */
export function getMemoryPath(cwd: string): string {
	const safePath = `--${cwd.replace(/^[/\\]/, "").replace(/[/\\:]/g, "-")}--`;
	return join(getMemoryDir(), `${safePath}.md`);
}

/** Load existing memory for a project, or return undefined if none exists */
export function loadMemory(cwd: string): string | undefined {
	const path = getMemoryPath(cwd);
	if (!existsSync(path)) return undefined;
	try {
		const content = readFileSync(path, "utf-8").trim();
		return content || undefined;
	} catch {
		return undefined;
	}
}

/** Save memory content for a project */
export function saveMemory(cwd: string, content: string): void {
	const dir = getMemoryDir();
	if (!existsSync(dir)) {
		mkdirSync(dir, { recursive: true });
	}
	writeFileSync(getMemoryPath(cwd), content, "utf-8");
}

/** Extract the concatenated text from an AssistantMessage's content blocks. */
function extractText(message: AssistantMessage): string {
	return message.content
		.filter((c): c is { type: "text"; text: string } => c.type === "text")
		.map((c) => c.text)
		.join("");
}

/** The prompt used to generate a session memory entry */
const MEMORY_GENERATION_PROMPT = `You are analyzing a coding session to extract persistent knowledge for future sessions.

Given the conversation below, extract ONLY information that would be valuable in future sessions working on the same codebase. Focus on:

1. **Architecture**: Project structure, key patterns, module boundaries
2. **Decisions**: Technical decisions made and their rationale
3. **Gotchas**: Bugs found, edge cases, things that didn't work
4. **Conventions**: Coding style preferences, naming conventions, tooling setup
5. **State**: What was accomplished, what's left unfinished

Rules:
- Be extremely concise. Each point should be one line.
- Skip generic knowledge (language features, common patterns)
- Only include project-specific knowledge the user would have to re-explain
- If the session was trivial (simple question, no real work), respond with "SKIP"
- Output as a markdown list grouped by category
- Max 20 bullet points total`;

/** The prompt used to merge new memory with existing */
const MEMORY_MERGE_PROMPT = `You are maintaining a persistent knowledge base for a coding project.

Below is the EXISTING memory, followed by NEW observations from a recent session.

Merge them into a single, deduplicated knowledge base:
- Remove outdated entries that the new session contradicts
- Deduplicate similar entries
- Keep the most recent/accurate version of conflicting info
- Preserve important architectural and decision context
- Stay concise: max 30 bullet points total
- Output as a markdown list grouped by category (Architecture, Decisions, Gotchas, Conventions, State)
- If the new observations say "SKIP", return the existing memory unchanged`;

/**
 * Generate a memory summary from a completed session's messages.
 * If existing memory exists, merges the new observations with it.
 */
export async function generateMemory(options: {
	messages: readonly AgentMessage[];
	model: Model<any>;
	apiKey: string;
	headers?: Record<string, string>;
	existingMemory?: string;
	cwd: string;
}): Promise<string | undefined> {
	const { messages, model, apiKey, headers, existingMemory, cwd } = options;

	// Skip very short sessions (less than 2 assistant messages)
	const assistantMessages = messages.filter(
		(m): m is AssistantMessage => "role" in m && (m as { role: string }).role === "assistant",
	);
	if (assistantMessages.length < 2) return existingMemory;

	// Serialize conversation to text (simplified -- just user and assistant text)
	const conversationLines: string[] = [];
	for (const msg of messages) {
		if (!("role" in msg)) continue;
		const m = msg as { role: string; content: unknown };
		if (m.role === "user" && Array.isArray(m.content)) {
			const text = (m.content as { type: string; text?: string }[])
				.filter((c) => c.type === "text")
				.map((c) => c.text ?? "")
				.join("\n");
			if (text) conversationLines.push(`User: ${text}`);
		} else if (m.role === "user" && typeof m.content === "string") {
			conversationLines.push(`User: ${m.content}`);
		} else if (m.role === "assistant" && Array.isArray(m.content)) {
			const blocks = m.content as { type: string; text?: string; name?: string }[];
			const text = blocks
				.filter((c) => c.type === "text")
				.map((c) => c.text ?? "")
				.join("\n");
			if (text) conversationLines.push(`Assistant: ${text.slice(0, 2000)}`);
			// Include tool call names
			const toolCalls = blocks.filter((c) => c.type === "toolCall");
			if (toolCalls.length > 0) {
				const toolNames = toolCalls.map((t) => t.name).join(", ");
				conversationLines.push(`  [Tools used: ${toolNames}]`);
			}
		}
	}

	// Truncate conversation to fit in context (keep last ~50 exchanges)
	const conversation = conversationLines.slice(-100).join("\n");
	const now = Date.now();

	try {
		if (existingMemory) {
			// Merge mode: generate new observations, then merge with existing
			const newObsResult = await completeSimple(
				model,
				{
					systemPrompt: MEMORY_GENERATION_PROMPT,
					messages: [
						{
							role: "user",
							content: `Project: ${cwd}\n\n<conversation>\n${conversation}\n</conversation>`,
							timestamp: now,
						},
					],
				},
				{ apiKey, headers },
			);

			const newObservations = extractText(newObsResult).trim();
			if (!newObservations || newObservations === "SKIP") {
				return existingMemory;
			}

			const mergedResult = await completeSimple(
				model,
				{
					systemPrompt: MEMORY_MERGE_PROMPT,
					messages: [
						{
							role: "user",
							content: `EXISTING MEMORY:\n${existingMemory}\n\nNEW OBSERVATIONS:\n${newObservations}`,
							timestamp: now,
						},
					],
				},
				{ apiKey, headers },
			);

			const merged = extractText(mergedResult).trim();
			return merged || existingMemory;
		} else {
			// Fresh memory generation
			const result = await completeSimple(
				model,
				{
					systemPrompt: MEMORY_GENERATION_PROMPT,
					messages: [
						{
							role: "user",
							content: `Project: ${cwd}\n\n<conversation>\n${conversation}\n</conversation>`,
							timestamp: now,
						},
					],
				},
				{ apiKey, headers },
			);

			const text = extractText(result).trim();
			if (!text || text === "SKIP") {
				return undefined;
			}

			return text;
		}
	} catch (err) {
		const msg = err instanceof Error ? err.message : String(err);
		console.error(`[memory] Failed to generate memory: ${msg}`);
		return existingMemory;
	}
}
